import { createReducer, createAction } from '@reduxjs/toolkit';
import i18n from 'i18n-js';

import { backendCall, createApiCall } from '@reducers/utils';
import { Alert } from '@helpers';
import { getDeviceDetails } from '@reducers/device';
import { sendLog } from '@reducers/dataDogs';
import { LOGGER_TITLES } from '@reducers/dataDogs/constants';
import type { RootState, AppThunk } from '@store/types';
import type { TicketState, FeedbackParams, SubmitFeedbackResponse } from './types';
import type { SubmitFeedbackRequestDto } from '@api/generated/models';
import { LOGS_AMOUNT_TO_SEND_IN_FEEDBACK } from '@v2/pages/common/constants/base.constants';

// Re-export types for consumers
export type { TicketState, FeedbackParams, SubmitFeedbackResponse } from './types';

// === Initial State ===

const initState: TicketState = {};

// === Selectors ===

// No meaningful selectors for this reducer

// === Actions ===

export const clear = createAction('clear');

// === Thunks ===

export const feedback =
  ({ type, message }: FeedbackParams): AppThunk<Promise<SubmitFeedbackResponse | undefined>> =>
  async (dispatch, getState) => {
    const {
      logs: { history, eventsHistory },
    } = getState();

    await dispatch(sendLog({ title: LOGGER_TITLES.USER_SIGNALR_HISTORY, data: { eventsHistory, history } }) as unknown as ReturnType<typeof sendLog>);

    const data: SubmitFeedbackRequestDto = {
      deviceInformation: await getDeviceDetails(),
      feedbackType: type,
      email: '',
      message,
      log: JSON.stringify({
        history: history.slice(0, LOGS_AMOUNT_TO_SEND_IN_FEEDBACK),
        eventsHistory: eventsHistory.slice(0, LOGS_AMOUNT_TO_SEND_IN_FEEDBACK),
      }),
    };

    return await dispatch(
      backendCall(
        createApiCall((api) => api.ticket.feedback, {
          params: data,
          onSuccess: () => {
            Alert({ title: i18n.t('Success'), type: 'success' });
          },
          onError: (error) => {
            console.log(error, 'error');
            Alert({ title: i18n.t('Error'), type: 'error' });
          },
        })
      )
    );
  };

// === Reducer ===

export default createReducer<TicketState>(initState, (builder) => {
  builder.addCase(clear, () => initState);
});
